import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmptyState from "../../common/EmptyState";
import FullPageLoader from "../../common/FullPageLoader";
import { adjustProductStock, getProductBySlug } from "../../api/productService";
import {
  dropdownColors,
  dropdownFonts,
  dropdownMaterials,
  dropdownProducts,
  dropdownSizes,
} from "../../api/dropdownService";
import { getErrorMessage } from "../../utils/errorHandler";

const variationAttributes = [
  { key: "sizeId", label: "Size" },
  { key: "materialId", label: "Material" },
  { key: "colorId", label: "Color" },
  { key: "fontId", label: "Font" },
];

const adjustmentReasons = [
  { value: "wastage", label: "Wastage" },
  { value: "damaged", label: "Damaged" },
  { value: "correction", label: "Correction" },
  { value: "other", label: "Other" },
];

const getProductId = (product) =>
  product?._id || product?.id || product?.productId || "";

const getAttributeId = (value) => {
  const attribute = Array.isArray(value) ? value[0] : value;
  if (attribute && typeof attribute === "object") {
    return attribute._id ? String(attribute._id) : "";
  }
  return attribute ? String(attribute) : "";
};

const getProductList = (response) => {
  const products =
    response?.data?.products ||
    response?.data?.data ||
    response?.products ||
    response?.data ||
    response ||
    [];

  return Array.isArray(products) ? products : [];
};

const getDropdownOptions = (response) => {
  const options = response?.data || response || [];
  return Array.isArray(options) ? options : [];
};

const getOptionLabel = (key, option) => {
  if (key === "colorId" || key === "fontId") {
    return option?.name || option?.value || "";
  }
  return option?.value || option?.name || "";
};

export default function StockAdjust() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [variants, setVariants] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [selections, setSelections] = useState({});
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchFormOptions = async () => {
      try {
        const [
          productResponse,
          sizesResponse,
          materialsResponse,
          colorsResponse,
          fontsResponse,
        ] = await Promise.all([
          dropdownProducts(),
          dropdownSizes(),
          dropdownMaterials(),
          dropdownColors(),
          dropdownFonts(),
        ]);

        if (cancelled) return;

        setProducts(getProductList(productResponse));
        setDropdownOptions({
          sizeId: getDropdownOptions(sizesResponse),
          materialId: getDropdownOptions(materialsResponse),
          colorId: getDropdownOptions(colorsResponse),
          fontId: getDropdownOptions(fontsResponse),
        });
      } catch (error) {
        if (!cancelled) {
          setProductsError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setProductsLoading(false);
        }
      }
    };

    fetchFormOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedProduct) return undefined;

    let cancelled = false;
    const fetchSelectedProduct = async () => {
      const slug = selectedProduct.slug || selectedProduct.seo?.slug;
      if (!slug) {
        setProductDetails(selectedProduct);
        setVariants(Array.isArray(selectedProduct.variants) ? selectedProduct.variants : []);
        setProductError("Product details are unavailable.");
        return;
      }

      setProductLoading(true);
      setProductError("");
      setProductDetails(null);
      setVariants([]);

      try {
        const response = await getProductBySlug(slug);
        if (cancelled) return;

        const data = response?.data;
        const details = data?.product || data;
        const productVariants = data?.variants || details?.variants || [];

        setProductDetails(details);
        setVariants(Array.isArray(productVariants) ? productVariants : []);
      } catch (error) {
        if (!cancelled) {
          setProductError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setProductLoading(false);
        }
      }
    };

    fetchSelectedProduct();

    return () => {
      cancelled = true;
    };
  }, [selectedProduct]);

  const normalizedVariants = useMemo(
    () =>
      variants.map((variant) => {
        const normalized = {};
        variationAttributes.forEach(({ key }) => {
          normalized[key] = getAttributeId(variant?.[key]);
        });
        return normalized;
      }),
    [variants],
  );

  const availableAttributes = useMemo(
    () =>
      variationAttributes
        .map((attribute) => {
          const ids = Array.from(
            new Set(normalizedVariants.map((variant) => variant[attribute.key]).filter(Boolean)),
          );
          if (ids.length === 0) return null;

          const optional =
            attribute.key !== "sizeId" &&
            normalizedVariants.some((variant) => !variant[attribute.key]);

          const options = ids.map((id) => {
            const variant = variants.find(
              (item) => getAttributeId(item?.[attribute.key]) === id,
            );
            const rawValue = Array.isArray(variant?.[attribute.key])
              ? variant[attribute.key][0]
              : variant?.[attribute.key];
            const populatedLabel =
              rawValue && typeof rawValue === "object"
                ? rawValue.value || rawValue.name || rawValue.title
                : "";
            const dropdownOption = dropdownOptions[attribute.key]?.find(
              (item) => String(item?._id) === id,
            );

            return {
              id,
              label:
                populatedLabel ||
                getOptionLabel(attribute.key, dropdownOption) ||
                id,
            };
          });

          return {
            ...attribute,
            optional,
            options,
          };
        })
        .filter(Boolean),
    [dropdownOptions, normalizedVariants, variants],
  );

  const matchingVariation = normalizedVariants.find(
    (variant) =>
      Boolean(variant.sizeId) &&
      availableAttributes.length > 0 &&
      availableAttributes.every(
        ({ key }) => (selections[key] || "") === variant[key],
      ),
  );
  const hasCompleteVariationSelection =
    availableAttributes.length > 0 &&
    availableAttributes.every(
      ({ key, optional }) => Boolean(selections[key]) || optional,
    );

  const handleProductChange = (event) => {
    const productId = event.target.value;
    const product =
      products.find((item) => String(getProductId(item)) === productId) || null;

    setSelectedProduct(product);
    setProductDetails(null);
    setVariants([]);
    setProductLoading(false);
    setSelections({});
    setQuantity("");
    setReason("");
    setNote("");
    setErrors({});
    setProductError("");
  };

  const handleSelectionChange = (event) => {
    const { name, value } = event.target;
    setSelections((previous) => ({
      ...previous,
      [name]: value,
    }));
    setErrors((previous) => ({
      ...previous,
      [name]: "",
      variation: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {};
    const numericQuantity = Number(quantity);
    if (!selectedProduct) {
      nextErrors.product = "Product is required.";
    }
    if (!matchingVariation) {
      nextErrors.variation = "Select a valid product variation.";
    }
    if (!quantity.trim() || !Number.isFinite(numericQuantity) || numericQuantity <= 0) {
      nextErrors.quantity = "Enter a quantity greater than zero.";
    }
    if (!adjustmentReasons.some((option) => option.value === reason)) {
      nextErrors.reason = "Select an adjustment reason.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const variationKey = { sizeId: selections.sizeId };
    availableAttributes.forEach(({ key }) => {
      if (key !== "sizeId" && selections[key]) {
        variationKey[key] = selections[key];
      }
    });

    try {
      setLoading(true);
      await adjustProductStock({
        productId: getProductId(selectedProduct),
        variationKey,
        quantity: numericQuantity,
        reason,
        ...(note.trim() ? { note: note.trim() } : {}),
      });

      navigate("/products");
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const displayedProduct = productDetails || selectedProduct;
  const productName =
    displayedProduct?.productName ||
    displayedProduct?.name ||
    displayedProduct?.title ||
    displayedProduct?.slug ||
    "";
  const productImage =
    displayedProduct?.seo?.metaImageUrl ||
    displayedProduct?.featuredImageUrl ||
    selectedProduct?.seo?.metaImageUrl ||
    "/default-product.png";

  return (
    <div className="container-fluid p-4">
      {loading && <FullPageLoader text="Saving..." />}
      <div className="card border-2">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Product<span className="error">*</span>
                </label>
                <select
                  className={`form-select ${errors.product ? "is-invalid" : ""}`}
                  value={getProductId(selectedProduct)}
                  title={
                    selectedProduct?.productName ||
                    selectedProduct?.name ||
                    selectedProduct?.title ||
                    selectedProduct?.slug ||
                    ""
                  }
                  onChange={handleProductChange}
                  disabled={productsLoading || loading}
                >
                  <option value="">
                    {productsLoading
                      ? "Loading products..."
                      : products.length
                        ? "Select Product"
                        : "No products available"}
                  </option>
                  {products.map((product) => {
                    const name =
                      product.productName || product.name || product.title || product.slug;
                    return (
                      <option
                        key={getProductId(product)}
                        value={getProductId(product)}
                        title={name}
                      >
                        {name}
                      </option>
                    );
                  })}
                </select>
                {errors.product && <div className="invalid-feedback">{errors.product}</div>}
                {!productsLoading && products.length === 0 && (
                  <EmptyState title="No products available" />
                )}
              </div>
            </div>

            {selectedProduct && (
              <div className="d-flex align-items-center gap-3 mb-3">
                <img
                  src={productImage}
                  alt={productName}
                  width="72"
                  height="72"
                  className="rounded"
                  style={{ objectFit: "cover" }}
                />
                <strong>{productName}</strong>
              </div>
            )}

            {productLoading && (
              <div className="text-muted mb-3">Loading product variations...</div>
            )}
            {productError && <div className="alert alert-danger">{productError}</div>}

            <div className="row">
              {variationAttributes.map(({ key, label }) => {
                const attribute = availableAttributes.find((item) => item.key === key);
                const optional = attribute?.optional ?? key !== "sizeId";
                const options = attribute?.options || [];

                return (
                  <div className="col-md-6 mb-3" key={key}>
                    <label className="form-label">
                      {label}
                      {!optional && <span className="error">*</span>}
                    </label>
                    <select
                      name={key}
                      className={`form-select ${
                        errors[key] || errors.variation ? "is-invalid" : ""
                      }`}
                      value={selections[key] || ""}
                      onChange={handleSelectionChange}
                      disabled={
                        !selectedProduct ||
                        productLoading ||
                        Boolean(productError) ||
                        loading ||
                        options.length === 0
                      }
                    >
                      <option value="">
                        {!selectedProduct || productLoading || !optional
                          ? `Select ${label}`
                          : options.length
                            ? "Not applicable"
                            : "Not available"}
                      </option>
                      {options.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            {!productLoading &&
              !productError &&
              selectedProduct &&
              variants.length === 0 && <EmptyState title="No variations available" />}

            {!productLoading &&
              !productError &&
              hasCompleteVariationSelection &&
              !matchingVariation && (
                <div className="alert alert-danger">
                  The selected variation is not available for this product.
                </div>
              )}
            {errors.variation && <div className="text-danger mb-3">{errors.variation}</div>}

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Quantity<span className="error">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="any"
                  className={`form-control ${errors.quantity ? "is-invalid" : ""}`}
                  value={quantity}
                  onChange={(event) => {
                    setQuantity(event.target.value);
                    setErrors((previous) => ({ ...previous, quantity: "" }));
                  }}
                  disabled={loading}
                  placeholder="Enter quantity"
                />
                {errors.quantity && <div className="invalid-feedback">{errors.quantity}</div>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Reason<span className="error">*</span>
                </label>
                <select
                  className={`form-select ${errors.reason ? "is-invalid" : ""}`}
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setErrors((previous) => ({ ...previous, reason: "" }));
                  }}
                  disabled={loading}
                >
                  <option value="">Select Reason</option>
                  {adjustmentReasons.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label">Note</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  disabled={loading}
                  placeholder="Enter an optional note"
                />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="submit"
                className="btn btn-primary"
              >
                {loading ? "Saving..." : "Adjust Stock"}
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  backgroundColor: "#032B4D",
                  color: "#fff",
                }}
                onClick={() => navigate("/products")}
                disabled={loading}
              >
                Cancel
              </button>
              
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
